import React, { useContext, useEffect, useState } from 'react';
import { classnames } from 'helpers/utils';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { useParams } from 'react-router-dom';
import { useMediaQuery } from 'hooks/use-media-query';
import { Spinner } from 'common/spinner';
import { SelfRecordingContext } from 'context/self-recording-context';
import { selfRecordingActions } from 'context/self-recording-context/action-types';
import { AppContext, appActions } from 'context/app-context';
import { InvalidGuestLink } from 'pages/invalid-guest-link';
import { VaultSwitcher } from 'common/vault-switcher';
import { goToPage, RouteName } from 'routes';
import { InterviewListItem } from 'common/interview-list-item';
import {
  Breakpoints,
  AlbumAddContentTypes,
  AlbumContentType,
  NotificationType,
} from 'common/enums';
import { Album } from 'models/album';
import { NotificationObject } from 'models/notificationObject';
import { ContentEntry } from 'common/album-detail-modal/components/content-entry';
import { AlbumUploadEntry } from 'common/album-detail-modal/components/album-upload-entry';
import { AlbumContentSerialized } from 'networking/types/album-detail';
import { ExpandCarousel } from 'common/expand-carousel';
import { GuestsController } from 'networking/controllers/guests-controller';
import { AlbumHeaderTitle } from '../../common/album-header-title';
import { AddContent } from '../../common/add-content';
import styles from './self-recording-guest.module.scss';
import { Vault } from '../../models/vault';

const SelfRecordingGuest: React.FC = () => {
  const { vaultId, guestToken, albumId } = useParams<{
    vaultId?: string;
    guestToken?: string;
    albumId?: string;
  }>();
  const { state, dispatch } = useContext(SelfRecordingContext);
  const { dispatch: appDispatch } = useContext(AppContext);
  const [albums, setAlbums] = useState<Album[]>([]);
  const [vault, setVault] = useState<VaultType | null>(null);
  const [fetching, setFetching] = useState(true);
  const [isError, setIsError] = useState(false);
  const [selectedAlbum, setSelectedAlbum] = useState<Album | null>(null);
  const [activeContentType, setActiveContentType] =
    useState<AlbumAddContentTypes | null>(null);
  const [newContentTitle, setNewContentTitle] = useState<string>('');
  const [showViewModeInIndex, setShowViewModeInIndex] = useState<
    number | undefined
  >(undefined);
  const [guestTokenId, setGuestTokenId] = useState<number | null>(null);
  // Editors can change every memory in the album; others only their own.
  const [guestRole, setGuestRole] = useState<AlbumMemberRoleValue | null>(null);
  const isSmallScreen = useMediaQuery(`(max-width: ${Breakpoints.sm}px)`);
  const hasContent = (selectedAlbum?.contents?.length ?? 0) > 0;

  const selectAlbum = (album: Album) => {
    setSelectedAlbum(album);
    setActiveContentType(null);
    setNewContentTitle('');
    setShowViewModeInIndex(undefined);
    dispatch({
      type: selfRecordingActions.setAlbumId,
      albumId: album.id,
    });

    if (vaultId) {
      dispatch({
        type: selfRecordingActions.setVaultId,
        vaultId: Number(vaultId),
      });
    }
  };

  const fetchAlbum = async () => {
    try {
      const fetchedAlbum = await VaultsController.getSharedAlbum(
        Number(albumId),
      );
      const album: Album = new Album({
        ...fetchedAlbum,
        globalOrderIndex: 0,
      });

      setAlbums([album]);
      selectAlbum(album);

      appDispatch({
        type: appActions.SET_CURRENT_VAULT,
        currentVault: new Vault({ id: Number(vaultId) } as VaultType),
      });
    } catch (_error) {
      setIsError(true);
    } finally {
      setFetching(false);
    }
  };

  const fetchVault = async () => {
    try {
      const response = await VaultsController.getVaultForGuest(
        Number(vaultId),
        true,
      );
      setVault(response as VaultType);

      const allAlbums = response.allAlbums || [];
      setAlbums(allAlbums);

      if (allAlbums.length > 0) {
        selectAlbum(allAlbums[0]);
      } else {
        setSelectedAlbum(null);
      }
    } catch (_error) {
      setIsError(true);
    } finally {
      setFetching(false);
    }
  };

  const fetchGuestInfo = async () => {
    if (!guestToken) {
      setGuestTokenId(null);
      setGuestRole(null);
      return;
    }

    try {
      const guestInfo = await GuestsController.getGuestInfo();
      setGuestTokenId(guestInfo.id);
      setGuestRole(guestInfo.accessLevel ?? null);
    } catch (_error) {
      setGuestTokenId(null);
      setGuestRole(null);
    }
  };

  const handleUpdateAlbumNameLocal = (newName: string) => {
    const targetAlbumId = selectedAlbum?.id;
    if (!targetAlbumId) return;

    setSelectedAlbum((prev) =>
      prev && prev.id === targetAlbumId
        ? new Album({ ...prev, name: newName })
        : prev,
    );
    setAlbums((prevAlbums) =>
      prevAlbums.map((m) =>
        m.id === targetAlbumId ? new Album({ ...m, name: newName }) : m,
      ),
    );
  };

  const handleCreateCustomTopic = async () => {
    try {
      const newCustomTopic = await VaultsController.createAlbum(
        Number(vaultId),
        'Custom Topic',
      );

      const minGlobalIndex = Math.min(
        ...albums.map((m) => m.globalOrderIndex),
        0,
      );
      const albumWithOrder = {
        ...newCustomTopic,
        globalOrderIndex: minGlobalIndex - 1,
      } as Album;

      setAlbums([albumWithOrder, ...albums]);
      selectAlbum(albumWithOrder);
      appDispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Custom topic added successfully',
          type: NotificationType.Success,
        }),
      });
    } catch (error) {
      appDispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Something went wrong',
          message: 'Failed to add custom topic',
          type: NotificationType.Error,
        }),
      });
    }
  };

  const handleUploadSuccess = async (uploadId: string) => {
    const response = state.uploadResponses[uploadId];
    if (!response) return;

    const mediaUrl =
      response.fileUrl || response.videoUrl || response.url || '';

    const id = response.contentId ? response.contentId : response.id;

    const newContent: AlbumContentSerialized = {
      id,
      content: response,
      contentType: response.contentType,
      createdAt: response.createdAt,
      updatedAt: response.updatedAt,
      description: '',
      fileDate: response.fileDate ?? null,
      fileSize: response.fileSize ?? null,
      isPrivate: response.isPrivate ?? false,
      length: null,
      mediaUrl,
      name:
        newContentTitle ||
        (response.contentType !== AlbumContentType.write && response.fileName
          ? ''
          : response.fileName || ''),
      thumbnailUrl: response.thumbnailUrl || '',
      startDate: response.startDate,
      endDate: response.endDate,
      user: response.user,
      guestTokenId,
    };

    const targetAlbumId = response.albumId ?? selectedAlbum?.id;
    if (!targetAlbumId) return;

    setSelectedAlbum((prevAlbum) => {
      if (!prevAlbum || prevAlbum.id !== targetAlbumId) return prevAlbum;
      return new Album({
        ...prevAlbum,
        contents: [newContent, ...(prevAlbum.contents || [])],
      });
    });
    setAlbums((prevAlbums) =>
      prevAlbums.map((album) =>
        album.id === targetAlbumId
          ? new Album({
              ...album,
              contents: [newContent, ...(album.contents || [])],
            })
          : album,
      ),
    );

    setNewContentTitle('');
    setActiveContentType(null);
  };

  const handleDeleteContentLocal = (contentId: number) => {
    if (!selectedAlbum?.id) return;
    const targetAlbumId = selectedAlbum.id;

    setSelectedAlbum((prevAlbum) => {
      if (!prevAlbum || prevAlbum.id !== targetAlbumId) return prevAlbum;
      return new Album({
        ...prevAlbum,
        contents: prevAlbum.contents.filter(
          (content) => content.id !== contentId,
        ),
      });
    });
    setAlbums((prevAlbums) =>
      prevAlbums.map((album) =>
        album.id === targetAlbumId
          ? new Album({
              ...album,
              contents: album.contents.filter((c) => c.id !== contentId),
            })
          : album,
      ),
    );
  };

  const handleContentUpdateLocal = (
    contentId: number,
    updatedJournalDocument: JournalDocumentType,
  ) => {
    if (!selectedAlbum?.id) return;
    const targetAlbumId = selectedAlbum.id;
    const applyUpdate = (content: AlbumContentSerialized) => {
      if (content.id !== contentId) return content;
      return {
        ...content,
        content: {
          ...content.content,
          id: updatedJournalDocument.id,
          body: updatedJournalDocument.body,
          name: updatedJournalDocument.name,
          location: updatedJournalDocument.location,
          entryDate: updatedJournalDocument.entryDate,
          startDate: updatedJournalDocument.startDate,
          endDate: updatedJournalDocument.endDate,
        },
        name: updatedJournalDocument.name,
      };
    };

    setSelectedAlbum((prevAlbum) => {
      if (!prevAlbum || prevAlbum.id !== targetAlbumId) return prevAlbum;
      return new Album({
        ...prevAlbum,
        contents: prevAlbum.contents.map(applyUpdate),
      });
    });
    setAlbums((prevAlbums) =>
      prevAlbums.map((album) =>
        album.id === targetAlbumId
          ? new Album({
              ...album,
              contents: album.contents.map(applyUpdate),
            })
          : album,
      ),
    );
  };

  const handleContentNameUpdateLocal = (contentId: number, newName: string) => {
    if (!selectedAlbum?.id) return;
    const targetAlbumId = selectedAlbum.id;
    const applyUpdate = (content: AlbumContentSerialized) =>
      content.id === contentId ? { ...content, name: newName } : content;

    setSelectedAlbum((prevAlbum) => {
      if (!prevAlbum || prevAlbum.id !== targetAlbumId) return prevAlbum;
      return new Album({
        ...prevAlbum,
        contents: prevAlbum.contents.map(applyUpdate),
      });
    });
    setAlbums((prevAlbums) =>
      prevAlbums.map((album) =>
        album.id === targetAlbumId
          ? new Album({
              ...album,
              contents: album.contents.map(applyUpdate),
            })
          : album,
      ),
    );
  };

  const showContent = () => (
    <>
      {showViewModeInIndex !== undefined && selectedAlbum && (
        <ExpandCarousel
          contents={selectedAlbum.contents ?? []}
          onClose={() => setShowViewModeInIndex(undefined)}
          initialIndex={showViewModeInIndex}
        />
      )}
      <div className={styles.leftSide}>
        {!albumId && vault && (
          <VaultSwitcher
            vault={vault}
            compact={isSmallScreen}
            onVaultSelect={(vaultIdentificator: number) => {
              goToPage(RouteName.SelfRecordingGuest, {
                vaultId: vaultIdentificator,
              });
            }}
          />
        )}
        <div className={styles.leftColumn}>
          <div className={styles.albumsList}>
            <div className={styles.albumsListItems}>
              {!albumId && (
                <InterviewListItem
                  title="Custom Topic"
                  type="customTopic"
                  onClick={handleCreateCustomTopic}
                />
              )}
              {albums.length === 0 ? (
                <div className={styles.emptyState}>
                  <span className="text__body__regular__small__gray700">
                    You haven&apos;t added any topics.
                  </span>
                </div>
              ) : (
                albums.map((album) => (
                  <InterviewListItem
                    key={album.id}
                    title={album.name}
                    type="unanswered"
                    album={album}
                    isSelected={selectedAlbum?.id === album.id}
                    onClick={() => selectAlbum(album)}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.contentContainer}>
        {selectedAlbum && (
          <>
            <AlbumHeaderTitle
              title={selectedAlbum.name}
              onTitleChange={handleUpdateAlbumNameLocal}
              albumId={selectedAlbum.id}
              editable={
                guestTokenId !== null &&
                (selectedAlbum.guestTokenId ?? null) === guestTokenId
              }
            />
            <AddContent
              displayType={hasContent ? 'button' : 'empty-state'}
              setAddNewContent={setActiveContentType}
              albumId={selectedAlbum.id}
              emptyStateClassName={styles.addContent}
            />
            {activeContentType && (
              <ContentEntry
                userName=""
                contentType={activeContentType}
                isCreatingNewContent
                onNewContentTitleChange={setNewContentTitle}
                onStopRecording={() => {
                  setActiveContentType(null);
                }}
              />
            )}
            {state.uploads.map((upload) => (
              <AlbumUploadEntry
                key={upload.uploadId}
                type={upload.type}
                data={upload.data}
                isUploading={upload.isUploading}
                uploadId={upload.uploadId}
                onSuccess={
                  selectedAlbum?.contents
                    ? () => handleUploadSuccess(upload.uploadId)
                    : () => {}
                }
              />
            ))}
            {selectedAlbum.contents?.map((content, index) => (
              <ContentEntry
                key={content.id}
                userName={content.user?.name || ''}
                content={content}
                index={index}
                draggable={false}
                isGuestView
                editable={
                  guestTokenId !== null &&
                  (guestRole === 'editor' ||
                    (content.guestTokenId ?? null) === guestTokenId)
                }
                onDeleteContent={() => handleDeleteContentLocal(content.id)}
                onShowViewMode={() => setShowViewModeInIndex(index)}
                onContentUpdate={handleContentUpdateLocal}
                onContentNameUpdate={handleContentNameUpdateLocal}
              />
            ))}
          </>
        )}
      </div>
    </>
  );

  useEffect(() => {
    if (albumId) {
      fetchAlbum();
    } else {
      fetchVault();
    }
    fetchGuestInfo();
  }, [guestToken, albumId, vaultId]);

  useEffect(() => {
    if (vault) {
      dispatch({
        type: selfRecordingActions.setVaultId,
        vaultId: vault.id,
      });
      appDispatch({
        type: appActions.SET_CURRENT_VAULT,
        currentVault: vault,
      });
    }

    return () => {
      appDispatch({
        type: appActions.SET_CURRENT_VAULT,
      });
    };
  }, [vault]);

  if (isError) return <InvalidGuestLink />;
  return (
    <div className={classnames(styles.container)}>
      {!fetching ? showContent() : <Spinner />}
    </div>
  );
};

export { SelfRecordingGuest };
